import { PlotlyHTMLElement } from 'features/plotly'
import { ChartDataPoint } from 'features/systemLens'
import { useCursorData } from 'features/timeSeries/CursorDataContext'
import { getInterpolatedValue } from 'features/timeSeries/utils/chartHelpers'
import React, { useCallback, useRef } from 'react'
import { EnhancedSensorEntry } from 'types'

interface UseInterpolatedHoverOptions {
  xAxisSensorId: string | null
  connectPoints: boolean
  chartData: ChartDataPoint[]
  allSensorsToRender: Array<{ id: string; name: string }>
  enhancedSensorEntries: EnhancedSensorEntry[]
  unitMapping: Record<string, string>
  updateCursorData: (
    sensorId: string,
    yValue: number | null,
    xValue: number | null,
    unit?: string
  ) => void
  setCursorPosition: (
    position: {
      x: number
      y: number
      clientX: number
      clientY: number
      xValue?: number
    } | null
  ) => void
}

export const useInterpolatedHover = ({
  xAxisSensorId,
  connectPoints,
  chartData,
  allSensorsToRender,
  enhancedSensorEntries,
  unitMapping,
  updateCursorData,
  setCursorPosition,
}: UseInterpolatedHoverOptions) => {
  const internalRef = useRef<HTMLDivElement | null>(null)
  const { getXValueAtCursor } = useCursorData()

  const handleMouseMove = useCallback(
    (e: React.MouseEvent) => {
      if (xAxisSensorId || !connectPoints || !internalRef.current) return

      const plotElement = internalRef.current.querySelector(
        '.js-plotly-plot'
      ) as PlotlyHTMLElement | null
      if (!plotElement) return

      const xValue = getXValueAtCursor(e, plotElement)
      if (xValue === null) return

      const plotRect = plotElement.getBoundingClientRect()
      const relativeX = e.clientX - plotRect.left
      const relativeY = e.clientY - plotRect.top

      setCursorPosition({
        x: relativeX,
        y: relativeY,
        clientX: e.clientX,
        clientY: e.clientY,
        xValue: xValue,
      })

      allSensorsToRender.forEach((sensor) => {
        const sensorEntry = enhancedSensorEntries.find(
          (e) => e.id === sensor.id
        )
        const displayName = sensorEntry?.data?.name || sensor.id || sensor.name

        const value = getInterpolatedValue(chartData, sensor.id, xValue)

        if (value !== null) {
          const unit = unitMapping[displayName] || 'No Unit'
          updateCursorData(sensor.id, value, xValue, unit)
        }
      })
    },
    [
      xAxisSensorId,
      connectPoints,
      chartData,
      allSensorsToRender,
      enhancedSensorEntries,
      unitMapping,
      updateCursorData,
      setCursorPosition,
      getXValueAtCursor,
    ]
  )

  return {
    internalRef,
    handleMouseMove,
  }
}
